import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Box,
  MenuItem,
  IconButton,
  Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';

const CambioAgujas = () => {
  const [caracteristicasOriginales, setCaracteristicasOriginales] = useState([]);
  const [maquinaSeleccionada, setMaquinaSeleccionada] = useState('');
  const [cambiosAgujas, setCambiosAgujas] = useState([]);
  const [maquinaFiltro, setMaquinaFiltro] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({
    machineNumber: '',
    modelo: '',
    marca: '',
    diametro: '',
    galga: '',
    tipoAguja1: '',
    origen1: '',
    cantidad1: '',
    tipoAguja2: '',
    origen2: '',
    cantidad2: '',
    tipoAguja3: '',
    origen3: '',
    cantidad3: '',
    tipoAguja4: '',
    origen4: '',
    cantidad4: '',
    fechaCambio: new Date().toISOString().split('T')[0]
  });

  // Cargar características de máquinas
  const fetchCaracteristicas = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch('/api/caracteristicas-maquina', { headers });
      
      if (response.ok) {
        const data = await response.json();
        const sortedData = Array.isArray(data) ? data.sort((a, b) => {
          const numA = parseInt(a.machineNumber?.replace(/\D/g, '') || '0');
          const numB = parseInt(b.machineNumber?.replace(/\D/g, '') || '0');
          return numA - numB;
        }) : [];
        setCaracteristicasOriginales(sortedData);
      }
    } catch (err) {
      console.error('Error al cargar características:', err);
      setError('Error al cargar las características');
    }
  };

  // Cargar cambios de agujas
  const fetchCambiosAgujas = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const url = maquinaFiltro 
        ? `/api/cambios-agujas/maquina/${maquinaFiltro}`
        : '/api/cambios-agujas';
      
      const response = await fetch(url, { headers });
      
      if (response.ok) {
        const data = await response.json();
        setCambiosAgujas(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error al cargar cambios de agujas:', err);
    }
  };

  useEffect(() => {
    fetchCaracteristicas();
    fetchCambiosAgujas();
  }, []);

  useEffect(() => {
    fetchCambiosAgujas();
  }, [maquinaFiltro]);

  // Manejar selección de máquina
  const handleMaquinaChange = (machineNumber) => {
    setMaquinaSeleccionada(machineNumber);
    const caracteristica = caracteristicasOriginales.find(c => c.machineNumber === machineNumber);
    
    if (caracteristica) {
      setForm({
        machineNumber: caracteristica.machineNumber || '',
        modelo: caracteristica.modelo || '',
        marca: caracteristica.marca || '',
        diametro: caracteristica.diametro || '',
        galga: caracteristica.galga || '',
        tipoAguja1: caracteristica.tipoAguja1 || '',
        origen1: caracteristica.origen1 || '',
        cantidad1: '', // Vacío para que el usuario lo llene
        tipoAguja2: caracteristica.tipoAguja2 || '',
        origen2: caracteristica.origen2 || '',
        cantidad2: '',
        tipoAguja3: caracteristica.tipoAguja3 || '',
        origen3: caracteristica.origen3 || '',
        cantidad3: '',
        tipoAguja4: caracteristica.tipoAguja4 || '',
        origen4: caracteristica.origen4 || '',
        cantidad4: '',
        fechaCambio: new Date().toISOString().split('T')[0]
      });
    }
  };

  // Manejar cambios en el formulario
  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
  };

  // Guardar cambios
  const handleGuardar = async () => {
    setError(null);
    setSuccess(null);

    if (!form.machineNumber) {
      setError('Debe seleccionar una máquina');
      return;
    }

    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };

    try {
      // Guardar cada cambio de aguja que tenga cantidad
      const cambios = [];
      
      if (form.tipoAguja1 && form.cantidad1) {
        cambios.push({
          machineNumber: form.machineNumber,
          tipoAguja: form.tipoAguja1,
          origen: form.origen1,
          cantidad: parseInt(form.cantidad1),
          fechaCambio: form.fechaCambio
        });
      }
      
      if (form.tipoAguja2 && form.cantidad2) {
        cambios.push({
          machineNumber: form.machineNumber,
          tipoAguja: form.tipoAguja2,
          origen: form.origen2,
          cantidad: parseInt(form.cantidad2),
          fechaCambio: form.fechaCambio
        });
      }
      
      if (form.tipoAguja3 && form.cantidad3) {
        cambios.push({
          machineNumber: form.machineNumber,
          tipoAguja: form.tipoAguja3,
          origen: form.origen3,
          cantidad: parseInt(form.cantidad3),
          fechaCambio: form.fechaCambio
        });
      }
      
      if (form.tipoAguja4 && form.cantidad4) {
        cambios.push({
          machineNumber: form.machineNumber,
          tipoAguja: form.tipoAguja4,
          origen: form.origen4,
          cantidad: parseInt(form.cantidad4),
          fechaCambio: form.fechaCambio
        });
      }

      if (cambios.length === 0) {
        setError('Debe ingresar al menos una cantidad para registrar el cambio');
        return;
      }

      // Enviar cada cambio
      for (const cambio of cambios) {
        const response = await fetch('/api/cambios-agujas', {
          method: 'POST',
          headers,
          body: JSON.stringify(cambio)
        });

        if (!response.ok) {
          throw new Error('Error al guardar cambio de agujas');
        }
      }

      setSuccess('Cambios de agujas registrados correctamente');
      setTimeout(() => setSuccess(null), 3000);
      
      // Limpiar formulario
      setMaquinaSeleccionada('');
      setForm({
        machineNumber: '',
        modelo: '',
        marca: '',
        diametro: '',
        galga: '',
        tipoAguja1: '',
        origen1: '',
        cantidad1: '',
        tipoAguja2: '',
        origen2: '',
        cantidad2: '',
        tipoAguja3: '',
        origen3: '',
        cantidad3: '',
        tipoAguja4: '',
        origen4: '',
        cantidad4: '',
        fechaCambio: new Date().toISOString().split('T')[0]
      });
      
      fetchCambiosAgujas();
    } catch (err) {
      console.error('Error al guardar:', err);
      setError('Error al guardar los cambios: ' + err.message);
    }
  };

  // Eliminar cambio
  const handleEliminar = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este registro?')) return;

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const response = await fetch(`/api/cambios-agujas/${id}`, {
        method: 'DELETE',
        headers
      });

      if (response.ok) {
        setSuccess('Registro eliminado correctamente');
        setTimeout(() => setSuccess(null), 3000);
        fetchCambiosAgujas();
      } else {
        setError('Error al eliminar el registro');
      }
    } catch (err) {
      setError('Error de conexión al servidor');
    }
  };

  // Formatear fecha
  const formatFecha = (fecha) => {
    return new Date(fecha).toLocaleString('es-CO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Container maxWidth="xl" sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ color: '#fff', fontFamily: 'Roboto', fontWeight: 300, mb: 3 }}>
        Cambio de Agujas
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Formulario */}
      <Paper sx={{ p: 3, mb: 4, backgroundColor: '#2a2a2a' }}>
        <Typography variant="h5" sx={{ color: '#4caf50', mb: 2, fontFamily: 'Roboto', fontWeight: 300 }}>
          Registrar Cambio de Agujas
        </Typography>
        
        <Grid container spacing={2}>
          {/* Selección de máquina */}
          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Máquina *"
              value={maquinaSeleccionada}
              onChange={(e) => handleMaquinaChange(e.target.value)}
              fullWidth
              sx={{
                '& .MuiInputLabel-root': { color: '#fff' },
                '& .MuiInputBase-root': { color: '#fff' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' }
              }}
            >
              <MenuItem value=""><em>Seleccione una máquina</em></MenuItem>
              {caracteristicasOriginales.map((c) => (
                <MenuItem key={c._id} value={c.machineNumber}>
                  Máquina {c.machineNumber}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Fecha de cambio */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Fecha de Cambio *"
              type="date"
              value={form.fechaCambio}
              onChange={(e) => handleChange('fechaCambio', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiInputLabel-root': { color: '#fff' },
                '& .MuiInputBase-root': { color: '#fff' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' }
              }}
            />
          </Grid>

          {/* Información general */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ color: '#4caf50', mt: 2, mb: 1 }}>
              Información de la Máquina
            </Typography>
          </Grid>
          
          <Grid item xs={6} md={3}>
            <TextField
              label="Modelo"
              value={form.modelo}
              fullWidth
              disabled
              sx={{
                '& .MuiInputLabel-root': { color: '#fff' },
                '& .MuiInputBase-root': { color: '#fff' },
                '& .Mui-disabled': { color: '#aaa', WebkitTextFillColor: '#aaa' }
              }}
            />
          </Grid>
          
          <Grid item xs={6} md={3}>
            <TextField
              label="Marca"
              value={form.marca}
              fullWidth
              disabled
              sx={{
                '& .MuiInputLabel-root': { color: '#fff' },
                '& .MuiInputBase-root': { color: '#fff' },
                '& .Mui-disabled': { color: '#aaa', WebkitTextFillColor: '#aaa' }
              }}
            />
          </Grid>
          
          <Grid item xs={6} md={3}>
            <TextField
              label="Diámetro"
              value={form.diametro}
              fullWidth
              disabled
              sx={{
                '& .MuiInputLabel-root': { color: '#fff' },
                '& .MuiInputBase-root': { color: '#fff' },
                '& .Mui-disabled': { color: '#aaa', WebkitTextFillColor: '#aaa' }
              }}
            />
          </Grid>
          
          <Grid item xs={6} md={3}>
            <TextField
              label="Galga"
              value={form.galga}
              fullWidth
              disabled
              sx={{
                '& .MuiInputLabel-root': { color: '#fff' },
                '& .MuiInputBase-root': { color: '#fff' },
                '& .Mui-disabled': { color: '#aaa', WebkitTextFillColor: '#aaa' }
              }}
            />
          </Grid>

          {/* Agujas 1 */}
          {form.tipoAguja1 && (
            <>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ color: '#4caf50', mt: 2, mb: 1 }}>
                  Agujas 1
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Tipo de Aguja 1"
                  value={form.tipoAguja1}
                  onChange={(e) => handleChange('tipoAguja1', e.target.value)}
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-root': { color: '#fff' },
                    '& .MuiInputBase-root': { color: '#fff' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Origen 1"
                  value={form.origen1}
                  onChange={(e) => handleChange('origen1', e.target.value)}
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-root': { color: '#fff' },
                    '& .MuiInputBase-root': { color: '#fff' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Cantidad 1 *"
                  type="number"
                  value={form.cantidad1}
                  onChange={(e) => handleChange('cantidad1', e.target.value)}
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-root': { color: '#fff' },
                    '& .MuiInputBase-root': { color: '#fff' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' }
                  }}
                />
              </Grid>
            </>
          )}

          {/* Agujas 2 */}
          {form.tipoAguja2 && (
            <>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ color: '#4caf50', mt: 2, mb: 1 }}>
                  Agujas 2
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Tipo de Aguja 2"
                  value={form.tipoAguja2}
                  onChange={(e) => handleChange('tipoAguja2', e.target.value)}
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-root': { color: '#fff' },
                    '& .MuiInputBase-root': { color: '#fff' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Origen 2"
                  value={form.origen2}
                  onChange={(e) => handleChange('origen2', e.target.value)}
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-root': { color: '#fff' },
                    '& .MuiInputBase-root': { color: '#fff' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Cantidad 2"
                  type="number"
                  value={form.cantidad2}
                  onChange={(e) => handleChange('cantidad2', e.target.value)}
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-root': { color: '#fff' },
                    '& .MuiInputBase-root': { color: '#fff' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' }
                  }}
                />
              </Grid>
            </>
          )}

          {/* Agujas 3 */}
          {form.tipoAguja3 && (
            <>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ color: '#4caf50', mt: 2, mb: 1 }}>
                  Agujas 3
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Tipo de Aguja 3"
                  value={form.tipoAguja3}
                  onChange={(e) => handleChange('tipoAguja3', e.target.value)}
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-root': { color: '#fff' },
                    '& .MuiInputBase-root': { color: '#fff' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Origen 3"
                  value={form.origen3}
                  onChange={(e) => handleChange('origen3', e.target.value)}
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-root': { color: '#fff' },
                    '& .MuiInputBase-root': { color: '#fff' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Cantidad 3"
                  type="number"
                  value={form.cantidad3}
                  onChange={(e) => handleChange('cantidad3', e.target.value)}
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-root': { color: '#fff' },
                    '& .MuiInputBase-root': { color: '#fff' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' }
                  }}
                />
              </Grid>
            </>
          )}

          {/* Agujas 4 */}
          {form.tipoAguja4 && (
            <>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ color: '#4caf50', mt: 2, mb: 1 }}>
                  Agujas 4
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Tipo de Aguja 4"
                  value={form.tipoAguja4}
                  onChange={(e) => handleChange('tipoAguja4', e.target.value)}
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-root': { color: '#fff' },
                    '& .MuiInputBase-root': { color: '#fff' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Origen 4"
                  value={form.origen4}
                  onChange={(e) => handleChange('origen4', e.target.value)}
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-root': { color: '#fff' },
                    '& .MuiInputBase-root': { color: '#fff' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Cantidad 4"
                  type="number"
                  value={form.cantidad4}
                  onChange={(e) => handleChange('cantidad4', e.target.value)}
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-root': { color: '#fff' },
                    '& .MuiInputBase-root': { color: '#fff' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' }
                  }}
                />
              </Grid>
            </>
          )}

          {/* Botón guardar */}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleGuardar}
                sx={{
                  backgroundColor: '#4caf50',
                  color: '#0a0a0aff',
                  '&:hover': { backgroundColor: '#45a049' }
                }}
              >
                Guardar Cambios
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabla de cambios registrados */}
      <Paper sx={{ p: 3, backgroundColor: '#2a2a2a' }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ color: '#4caf50', fontFamily: 'Roboto', fontWeight: 300 }}>
            Historial de Cambios
          </Typography>
          <TextField
            select
            label="Filtrar por Máquina"
            value={maquinaFiltro}
            onChange={(e) => setMaquinaFiltro(e.target.value)}
            sx={{
              minWidth: 200,
              '& .MuiInputLabel-root': { color: '#fff' },
              '& .MuiInputBase-root': { color: '#fff' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' }
            }}
          >
            <MenuItem value="">Todas las máquinas</MenuItem>
            {caracteristicasOriginales.map((c) => (
              <MenuItem key={c._id} value={c.machineNumber}>
                Máquina {c.machineNumber}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#1a1a1a' }}>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Máquina</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Tipo de Aguja</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Origen</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Cantidad</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Fecha de Cambio</TableCell>
                <TableCell sx={{ color: '#fff', fontWeight: 'bold' }} align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cambiosAgujas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ color: '#fff', py: 3 }}>
                    No hay cambios registrados
                  </TableCell>
                </TableRow>
              ) : (
                cambiosAgujas.map((cambio, index) => (
                  <TableRow
                    key={cambio._id}
                    sx={{
                      '&:hover': { backgroundColor: '#3a3a3a' },
                      backgroundColor: index % 2 === 0 ? '#2a2a2a' : '#323232'
                    }}
                  >
                    <TableCell sx={{ color: '#fff' }}>{cambio.machineNumber}</TableCell>
                    <TableCell sx={{ color: '#fff' }}>{cambio.tipoAguja}</TableCell>
                    <TableCell sx={{ color: '#fff' }}>{cambio.origen || '-'}</TableCell>
                    <TableCell sx={{ color: '#fff' }}>{cambio.cantidad}</TableCell>
                    <TableCell sx={{ color: '#fff' }}>{formatFecha(cambio.fechaCambio)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Eliminar">
                        <IconButton onClick={() => handleEliminar(cambio._id)} sx={{ color: '#f44336' }} size="small">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default CambioAgujas;
